import moment from 'moment';
import * as React from 'react';
import { getRepeatableSpace } from '../../../content/getRepeatableSpace/getRepeatableSpace';
import { SpaceContext } from '../../../content/types/SpaceContext';
import useDictionary from '../../../dictionaries/context/useDictionary';
import { NegotiationOption, NegotiationOptionAlternative, OutDebt } from '../../../generated/graphql';
import { getDiscountValue } from '../../../utils/getDiscountValue';
import { getNegBooleans } from '../../../utils/getNegBooleans';
import { rateConversion } from '../../../utils/rateConversion';
import { pluralize } from '../../../utils/pluralize';
import thousandSeparator from '../../../utils/thousandSeparator';
import { useGetProductNumberDef } from '../../../utils/useGetProductNumberDef';
import { NewPaymentTermsRowType, NewPaymentTermsRows } from './NewPaymentTermsRows';

interface NewPaymentTermsRowsConnectedProps {
  className?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  outDebt: OutDebt;
  // eslint-disable-next-line react/no-unused-prop-types
  negOption: NegotiationOption;
  // eslint-disable-next-line react/no-unused-prop-types
  alternative?: NegotiationOptionAlternative;
}

const useGetRows = (props: NewPaymentTermsRowsConnectedProps) => {
  const { gT } = useDictionary();

  const { outDebt, negOption, alternative: propsAlt } = props;
  const { negotiation } = outDebt;
  const {
    hasNeg, isConfirmedByUser, isTotalPayment, isPaymentPromise,
    isRappiPay, isRappiPrestamo, isBbva, isBeta, isBogotaLowRate, isACPK, isBogota,
    isInstallmentsPlan, isCondonation, isQuotification, isMovistarMoney, isRefinance,
    isBogotaPrepaymentPreferentialRate, isBogotaPrepaymentWeightedRate,
    isPichincha, isExpressRefinance, isSentToBank, isApprovedByBank, isCatchUp,
    isBalanceBehindPayment, isBogotaTraditional, isApex, isVirtualDebt, isJcap, isPra, isClaro,
    isBogotaTermExtension,
  } = getNegBooleans(outDebt, negOption, propsAlt);
  const alternative = negotiation?.alternative || propsAlt;
  const rows: NewPaymentTermsRowType[] = [];
  const add = (
    label: string, value?: string,
    tooltip?: NewPaymentTermsRowType['tooltip'],
  ) => rows.push({ label, value, tooltip });

  const productNumberDef = useGetProductNumberDef(outDebt);

  const maxPaymentDate = negotiation?.extra.info?.maxPaymentDate
  || negOption.settings?.maxPaymentDate;

  if (!alternative) return rows;
  const { prePayment, term, value, discountValue, commissionPercent } = alternative;

  const spaceContext: SpaceContext = {
    bank: outDebt.bank,
    brand: outDebt.brand!,
    country: outDebt.country!,
    outDebt,
    option: negOption,
    alternative,
  };

  // dictionary context for gT
  const dictContext = {
    productNumberDef,
    maxPaymentDate,
    outDebt,
    option: negOption,
    alternative,
    optionRate: isBogotaTermExtension
      ? `${parseFloat((rateConversion(negOption.rate || 0, 'm', 'y') * 100).toFixed(2))}%`
      : `${((negOption.rate || 0) * 100).toFixed(2)}%`,
    effectiveAnnualRate: alternative.effectiveAnnualRate == null
      ? ''
      : `${(alternative.effectiveAnnualRate * 100).toFixed(2)}%`,
  };

  // transform repeatable space to legacy rows
  const repeatableSpace = getRepeatableSpace('NEG_OPTION_TERMS', 'rows', spaceContext);
  if (repeatableSpace.exists) {
    repeatableSpace.rows.forEach((row) => {
      const [ label, val ] = row.getMany([ 'label', 'value' ]);
      // if at least one is visible
      if (!label?.visible || !val?.visible) return;
      const [ labelText, valueText ] = [ gT(label.key, dictContext), gT(val.key, dictContext) ];
      // the OR allows us to have wide rows with only label or only text
      if (labelText || valueText) add(labelText, valueText);
    });
    return rows;
  }

  const tooltip = {
    title: 'Ten en cuenta',
    text: `Este es el pago inicial que debes realizar para que la entidad pueda procesar
    tu negociación, este pago se abona a la deuda.`,
  };

  const bdbDateFormat = 'D MMM YYYY';
  const addMaxPaymentDate = (format = bdbDateFormat) => add('Fecha máxima de pago', moment(maxPaymentDate).format(format));
  const addBdbProduct = () => add('Producto', productNumberDef);
  const addBdbPaymentsAccount = () => add('Realizar el pago a la cuenta SNR', '(Sistema Nacional de Recaudo)');
  const addBdBTerm = () => add('Plazo Nuevo', `${term} cuota${pluralize(term)}`);
  const addBdBValue = (label = 'Cuota mensual estimada') => add(label, `$${thousandSeparator(value)}`);
  if (isBogota && isTotalPayment) {
    addBdbPaymentsAccount();
    addBdbProduct();
    add('Pago requerido', `$${thousandSeparator(value)}`);
  }
  if (isBogota && isBalanceBehindPayment) {
    if (isBogotaTraditional) {
      addBdbPaymentsAccount();
      addBdbProduct();
      addBdBValue('Pago requerido');
      add('Fecha de pago requerido', moment(maxPaymentDate).format(bdbDateFormat));
      if (!!negotiation && isConfirmedByUser) {
        addMaxPaymentDate();
        add('Abono inicial', `$${thousandSeparator(value)}`, tooltip);
      }
    }
    if (isBogotaLowRate) {
      if (isBogotaLowRate && outDebt?.extra?.popupRate) add('Reducción de tasa', `${outDebt.extra.popupRate}%`);
      addBdbPaymentsAccount();
    }
    return rows;
  }

  if (
    maxPaymentDate
    && isConfirmedByUser
    && ((isJcap || isPra) && negotiation)
    && (isTotalPayment || (!!prePayment && !(isBogota && isRefinance)))
  ) {
    add('Fecha máxima de pago', moment(maxPaymentDate).format('D MMM YYYY'));
  }

  if (
    maxPaymentDate
    && isConfirmedByUser
    && isClaro
  ) {
    add('Fecha máxima de pago', moment(maxPaymentDate).format('D MMM YYYY'));
  }

  if (isApex && (isVirtualDebt || isACPK)) {
    add('FECHA MAXIMA DE PAGO PRIMERA CUOTA', moment(maxPaymentDate).format('D MMM YYYY'));
  }

  if (isPaymentPromise) return rows;

  const isAcpkMovistar = isMovistarMoney && isACPK;

  if (isAcpkMovistar && value) add('Valor total de negociación', `$${thousandSeparator(value * term)}`);

  const discountPercent = !!alternative?.discountPercent
  && getDiscountValue(alternative.discountPercent);
  const discountPercentText = discountPercent ? `(${discountPercent}%)` : '';

  const discountLabel = (isBogota && isTotalPayment) ? 'Ajuste total aprox.' : `Descuento total aprox. ${discountPercentText}`;
  if (discountValue && discountValue > 0) add(discountLabel, `$${thousandSeparator(discountValue)}`);

  if (isBogota && isRefinance) {
    addBdBTerm();
    addBdBValue();
    addBdbProduct();
    const addDateBdB = isBogotaTraditional
      || isBogotaPrepaymentPreferentialRate || isBogotaPrepaymentWeightedRate;
    if (addDateBdB) add('Fecha límite para el abono inicial', moment(maxPaymentDate).format('D MMM YYYY'));
  }

  if (isMovistarMoney && isCatchUp) { // Pronto pago Movistar
    if (isConfirmedByUser) {
      add('Saldo total', `$ ${thousandSeparator(outDebt.balancePrincipal)}`);
      add('Fecha máxima de pago', moment(maxPaymentDate).format('D MMM YYYY'));
      return rows;
    }
    if (isSentToBank) {
      return rows;
    }
  }

  if (prePayment && !isBeta && isConfirmedByUser && !isAcpkMovistar && !(isBogota && isRefinance)) {
    add('Abono inicial', `$${thousandSeparator(prePayment)}`, tooltip);
  }

  const isRappiProductNumber = (!isRappiPay || !isRappiPrestamo)
  || isCondonation || isQuotification;
  if (hasNeg
    && !(isBogota && (isInstallmentsPlan || isTotalPayment || isRefinance))
    && isRappiProductNumber
    && !isBbva
    && (!isApex && (!isACPK))
    && !isMovistarMoney
    && (!isExpressRefinance && !isApprovedByBank)) add('Pagar al crédito número', productNumberDef);

  if (hasNeg && isMovistarMoney) {
    if (!isApprovedByBank) add('Pagar a la factura número', productNumberDef);
    if (isACPK && !isConfirmedByUser) {
      const payments = outDebt.payments?.filter(
        (p) => moment(p.date).isAfter(moment(negotiation?.createdAt)),
      );

      const values = payments?.reduce((acc, p) => acc + p.value, 0);
      add('Pago recibido', `$${thousandSeparator(values)}`);
    }
  }

  if (hasNeg && isBbva) add('Pagar al contrato número', productNumberDef);

  if (isTotalPayment && hasNeg) {
    if (isConfirmedByUser && (isMovistarMoney || isPichincha) && isTotalPayment) add('Valor final a pagar', `$${thousandSeparator(value)}`);
    return rows;
  }

  // plazo
  const termPrefix = !isTotalPayment && !hasNeg ? 'Nuevo' : '';
  const termSuffix = hasNeg ? 'Solicitado' : '';
  const termTooltip = {
    title: 'Ten en cuenta',
    text: `Este es el nuevo plazo solicitado, una vez realices el pago inicial la entidad
    procederá a aplicarlo. Si no realizas el pago inicial perderás está negociación.`,
  };
  if (
    !isCondonation && !isTotalPayment && !isPichincha
    && (!isInstallmentsPlan && !isBbva && !isBogota)
  ) {
    add(`${termPrefix} Plazo ${termSuffix}`, `${term} cuota${pluralize(term)}`,
      !hasNeg && prePayment ? termTooltip : undefined);
  }

  if (isPichincha && isExpressRefinance) {
    const { originalTerm } = outDebt.extra!;
    if (!originalTerm) return rows;
    if (
      (isSentToBank || isApprovedByBank || isConfirmedByUser)
      && (originalTerm || term)
    ) {
      add('Cuotas pendientes por pagar', `${term} cuota${pluralize(term)}`);
      if (!isConfirmedByUser) {
        add('Nueva cuota aprox:', `$${thousandSeparator(value)}`);
      }
      if (isSentToBank || isApprovedByBank) {
        add('Plazo inicial del crédito', `${(originalTerm || '').toString()} cuota${pluralize(originalTerm || 0)}`);
      }
    }
  }

  if (isConfirmedByUser && !isCondonation && (isMovistarMoney && !isTotalPayment)) add('Cuota solicitada aprox.', `$${thousandSeparator(value)}`);

  if (isBbva && isInstallmentsPlan) { // Refis
    const { originalTerm, disbursementDate } = outDebt.extra!;
    if (!originalTerm || !disbursementDate) return rows;
    add('Plazo original', `${originalTerm} cuota${pluralize(originalTerm)}`);
    const termsToPay = Math.round(moment().diff(moment(disbursementDate), 'months', true));
    add('Cuotas facturadas del plazo original', `${termsToPay} cuota${pluralize(termsToPay)}`);
    add('Nuevo Plazo', `${term} cuota${pluralize(term)}`);
  }

  const isBdbInstallmentsPlan = isBogota && isInstallmentsPlan;
  if (isBdbInstallmentsPlan) {
    addBdbProduct();
    addBdBValue();
    if (commissionPercent) add('Tasa nueva', `${(commissionPercent * 100).toFixed(2)}% E.A`);
    addBdBTerm();
  }
  return rows;
};

const NewPaymentTermsRowsConnected: React.FC<NewPaymentTermsRowsConnectedProps> = (props) => {
  const { className } = props;
  const rows = useGetRows(props);

  return (
    <NewPaymentTermsRows
      className={className}
      rows={rows}
    />
  );
};

export default NewPaymentTermsRowsConnected;
